import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Loader from '../../Components/Loader';
import CourierModeChargeService from '../../Services/CourierModeChargeService';

const money = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number.toFixed(2) : '0.00';
};

const plain = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? String(number) : '';
};

const downloadFile = (filename, csv) => {
    const link = document.createElement('a');
    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.download = filename;
    link.click();
};

const downloadSample = (mode) => {
    downloadFile(`${mode}-reimbursement-sample.csv`, 'Country,Charges on Duty/Taxes (%),Minimum\n');
};

const downloadCurrent = (mode, rows) => {
    const lines = ['Country,Charges on Duty/Taxes (%),Minimum'];
    rows.forEach((row) => {
        lines.push(`${row.country_name},${money(row.duty_tax_percent)}%,${money(row.minimum_amount)}`);
    });
    downloadFile(`${mode}-reimbursement-charges.csv`, lines.join('\n'));
};

const downloadOriginSample = (mode) => {
    downloadFile(`${mode}-certificate-of-origin-sample.csv`, 'Country,Value of Goods,Chargeable Wt,Charge\n');
};

const downloadRemoteSheet = (sample) => {
    const params = { charge_type: 'remote_surcharge' };
    if (sample) params.sample = 1;
    return CourierModeChargeService.exportRemote(params).then((res) => {
        if (res && res.status && res.data) {
            window.location.href = process.env.REACT_APP_API_BASEURL + '/' + res.data;
            return res;
        }
        toast.error((res && res.message) || 'Could not download the file');
        return res;
    });
};

const downloadOriginList = (mode, rows) => {
    const lines = ['Country,Value of Goods,Chargeable Wt,Charge'];
    rows.forEach((row) => {
        lines.push(`${row.country_name},Above ${plain(row.goods_value_above)},Above ${plain(row.chargeable_weight_above)} kgs,${plain(row.charge_amount)}`);
    });
    downloadFile(`${mode}-certificate-of-origin.csv`, lines.join('\n'));
};

export const ModeCharges = ({ mode }) => {
    const chargeMode = mode === 'land' ? 'land' : 'courier';
    const [tab, setTab] = useState('reimbursement');
    const [loader, setLoader] = useState(true);
    const [rows, setRows] = useState([]);
    const [form, setForm] = useState({ country_name: '', duty_tax_percent: '', minimum_amount: '' });
    const [originForm, setOriginForm] = useState({ country_name: '', goods_value_above: '', chargeable_weight_above: '', charge_amount: '' });
    const [remoteForm, setRemoteForm] = useState({ country_name: '', postal_code: '', tier: '' });
    const [tierForm, setTierForm] = useState({ tier: '', charge_amount: '', minimum_amount: '' });
    const [tierOptions, setTierOptions] = useState([]);
    const [search, setSearch] = useState('');

    const loadRows = (chargeType) => {
        setLoader(true);
        setRows([]);
        CourierModeChargeService.list({ mode: chargeMode, charge_type: chargeType }).then((res) => {
            setRows(res && res.status && Array.isArray(res.data) ? res.data : []);
            setLoader(false);
            if (res && res.status === false) toast.error(res.message || 'Could not load charges');
        }).catch(() => {
            setRows([]);
            setLoader(false);
        });
    };

    const loadTierOptions = () => {
        if (chargeMode !== 'courier') return;
        CourierModeChargeService.list({ mode: 'courier', charge_type: 'remote_tier' }).then((res) => {
            setTierOptions(res && res.status && Array.isArray(res.data) ? res.data : []);
        }).catch(() => setTierOptions([]));
    };

    useEffect(() => {
        if (tab !== 'reimbursement' && tab !== 'origin' && tab !== 'remote' && tab !== 'tier') return;
        setSearch('');
        if (tab === 'tier') {
            loadRows('remote_tier');
            return;
        }
        if (tab === 'remote') loadTierOptions();
        const chargeType = tab === 'origin'
            ? 'certificate_of_origin'
            : tab === 'remote'
                ? 'remote_surcharge'
                : 'reimbursement';
        loadRows(chargeType);
    }, [tab, chargeMode]);

    const onUpload = (event, chargeType) => {
        const file = event.target.files && event.target.files[0];
        event.target.value = '';
        if (!file) return;
        const formData = new FormData();
        formData.append('csv_file', file);
        formData.append('charge_type', chargeType);
        formData.append('mode', chargeMode);
        setLoader(true);
        CourierModeChargeService.importFile(formData).then((res) => {
            if (res && res.status) {
                toast.success(res.message || 'File uploaded');
                loadRows(chargeType);
            } else {
                setLoader(false);
                toast.error((res && res.message) || 'Upload failed');
            }
        });
    };

    const onSave = (event) => {
        event.preventDefault();
        CourierModeChargeService.save({ ...form, charge_type: 'reimbursement', mode: chargeMode }).then((res) => {
            if (res && res.status) {
                toast.success(res.message || 'Saved');
                setForm({ country_name: '', duty_tax_percent: '', minimum_amount: '' });
                loadRows('reimbursement');
            } else {
                toast.error((res && res.message) || 'Could not save');
            }
        });
    };

    const onSaveOrigin = (event) => {
        event.preventDefault();
        CourierModeChargeService.save({ ...originForm, charge_type: 'certificate_of_origin', mode: chargeMode }).then((res) => {
            if (res && res.status) {
                toast.success(res.message || 'Saved');
                setOriginForm({ country_name: '', goods_value_above: '', chargeable_weight_above: '', charge_amount: '' });
                loadRows('certificate_of_origin');
            } else {
                toast.error((res && res.message) || 'Could not save');
            }
        });
    };

    const onSaveTier = (event) => {
        event.preventDefault();
        CourierModeChargeService.save({ ...tierForm, charge_type: 'remote_tier', mode: 'courier' }).then((res) => {
            if (res && res.status) {
                toast.success(res.message || 'Saved');
                setTierForm({ tier: '', charge_amount: '', minimum_amount: '' });
                loadRows('remote_tier');
            } else {
                toast.error((res && res.message) || 'Could not save');
            }
        });
    };

    const onSaveRemote = (event) => {
        event.preventDefault();
        CourierModeChargeService.save({ ...remoteForm, charge_type: 'remote_surcharge', mode: chargeMode }).then((res) => {
            if (res && res.status) {
                toast.success(res.message || 'Saved');
                setRemoteForm({ country_name: '', postal_code: '', tier: '' });
                loadRows('remote_surcharge');
            } else {
                toast.error((res && res.message) || 'Could not save');
            }
        });
    };

    const query = search.trim().toLowerCase();
    const visibleRows = query
        ? rows.filter((row) => (
            String(row.country_name || '').toLowerCase().includes(query)
            || String(row.duty_tax_percent ?? '').toLowerCase().includes(query)
            || String(row.minimum_amount ?? '').toLowerCase().includes(query)
            || String(row.goods_value_above ?? '').toLowerCase().includes(query)
            || String(row.chargeable_weight_above ?? '').toLowerCase().includes(query)
            || String(row.charge_amount ?? '').toLowerCase().includes(query)
            || String(row.postal_code ?? '').toLowerCase().includes(query)
            || String(row.tier ?? '').toLowerCase().includes(query)
        ))
        : rows;

    const remoteGroups = Array.from(visibleRows.reduce((groups, row) => {
        const name = row.country_name || '';
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(row);
        return groups;
    }, new Map()).entries());
    const remoteLongest = remoteGroups.reduce((count, [, list]) => Math.max(count, list.length), 0);
    const tierPrice = (tier) => tierOptions.find((row) => String(row.tier || '').trim().toUpperCase() === String(tier || '').trim().toUpperCase());

    const onRemove = (row, chargeType) => {
        CourierModeChargeService.remove({ _id: row._id }).then((res) => {
            if (res && res.status) {
                toast.success(chargeType === 'remote_tier' ? 'Tier removed' : 'Removed');
                loadRows(chargeType);
            } else {
                toast.error((res && res.message) || 'Could not remove');
            }
        });
    };

    return (
        <div className="container-fluid">
            <div className="white_box">
                <div className="card">
                    <div className="card-body">
                        {loader && <Loader isShow={loader} />}
                        <h4 className="mb-1">{chargeMode === 'land' ? 'Land Mode Charges' : 'Courier Mode Charges'}</h4>
                        <p className="text-muted mb-3">
                            Country-wise extra charges for {chargeMode}.
                        </p>
                        <ul className="nav nav-tabs mb-4">
                            <li className="nav-item">
                                <button type="button" className={`nav-link ${tab === 'reimbursement' ? 'active' : ''}`} onClick={() => setTab('reimbursement')}>
                                    Reimbursement Charges
                                </button>
                            </li>
                            <li className="nav-item">
                                <button type="button" className={`nav-link ${tab === 'origin' ? 'active' : ''}`} onClick={() => setTab('origin')}>
                                    Certificate of Origin
                                </button>
                            </li>
                            {chargeMode === 'courier' && (
                            <li className="nav-item">
                                <button type="button" className={`nav-link ${tab === 'remote' ? 'active' : ''}`} onClick={() => setTab('remote')}>
                                    Remote Surcharge
                                </button>
                            </li>
                            )}
                            {chargeMode === 'courier' && (
                            <li className="nav-item">
                                <button type="button" className={`nav-link ${tab === 'tier' ? 'active' : ''}`} onClick={() => setTab('tier')}>
                                    Tier Price
                                </button>
                            </li>
                            )}
                        </ul>

                        {tab === 'reimbursement' && (
                            <>
                                <p className="mb-3">
                                    The charge is the percentage of duty and taxes, or the minimum amount, whichever is higher.
                                    File columns: <b>Country</b>, <b>Charges on Duty/Taxes (%)</b>, <b>Minimum</b>.
                                    Uploading adds new countries and updates countries already in the file. Other saved countries stay.
                                </p>
                                <div className="d-flex flex-wrap justify-content-end mb-3">
                                    <button type="button" className="btn btn-outline-secondary mx-1" onClick={() => downloadSample(chargeMode)}>Sample CSV</button>
                                    <input id="reimbursement-upload" type="file" accept=".csv,.xlsx,.xls" hidden onChange={(event) => onUpload(event, 'reimbursement')} />
                                    <label htmlFor="reimbursement-upload" className="btn btn-danger mx-1 mb-0">Upload file</label>
                                </div>
                                <form className="row align-items-end mb-4" onSubmit={onSave}>
                                    <div className="col-md-4 mb-2">
                                        <label>Country</label>
                                        <input className="form-control" value={form.country_name} onChange={(event) => setForm({ ...form, country_name: event.target.value })} placeholder="Country" />
                                    </div>
                                    <div className="col-md-3 mb-2">
                                        <label>Charges on Duty/Taxes (%)</label>
                                        <input className="form-control" value={form.duty_tax_percent} onChange={(event) => setForm({ ...form, duty_tax_percent: event.target.value })} placeholder="Charges on Duty/Taxes (%)" />
                                    </div>
                                    <div className="col-md-3 mb-2">
                                        <label>Minimum (AED)</label>
                                        <input className="form-control" value={form.minimum_amount} onChange={(event) => setForm({ ...form, minimum_amount: event.target.value })} placeholder="Minimum" />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <button type="submit" className="btn btn-primary w-100">Save</button>
                                    </div>
                                </form>
                                <div className="d-flex flex-wrap align-items-center mb-3">
                                    <input
                                        className="form-control"
                                        style={{ maxWidth: 320 }}
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        placeholder="Search"
                                    />
                                    <button type="button" className="btn btn-success mx-2" onClick={() => downloadCurrent(chargeMode, rows)} disabled={!rows.length}>Download list</button>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-centered table-nowrap mb-0 border text-center">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Country</th>
                                                <th>Charges on Duty/Taxes (%)</th>
                                                <th>Minimum (AED)</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleRows.length ? visibleRows.map((row) => (
                                                <tr key={row._id}>
                                                    <td>{row.country_name}</td>
                                                    <td>{money(row.duty_tax_percent)}%</td>
                                                    <td>{money(row.minimum_amount)}</td>
                                                    <td>
                                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(row, 'reimbursement')}>Remove</button>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="4">{rows.length ? 'No matching country.' : 'No countries yet. Upload the CSV or add one country.'}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {tab === 'origin' && (
                            <>
                                <p className="mb-3">
                                    The charge applies when the goods value is above the limit or the chargeable weight is above the limit.
                                    File columns: <b>Country</b>, <b>Value of Goods</b>, <b>Chargeable Wt</b>, <b>Charge</b>.
                                    Uploading adds new countries and updates countries already in the file. Other saved countries stay.
                                </p>
                                <div className="d-flex flex-wrap justify-content-end mb-3">
                                    <button type="button" className="btn btn-outline-secondary mx-1" onClick={() => downloadOriginSample(chargeMode)}>Sample CSV</button>
                                    <input id="origin-upload" type="file" accept=".csv,.xlsx,.xls" hidden onChange={(event) => onUpload(event, 'certificate_of_origin')} />
                                    <label htmlFor="origin-upload" className="btn btn-danger mx-1 mb-0">Upload file</label>
                                </div>
                                <form className="row align-items-end mb-4" onSubmit={onSaveOrigin}>
                                    <div className="col-md-3 mb-2">
                                        <label>Country</label>
                                        <input className="form-control" value={originForm.country_name} onChange={(event) => setOriginForm({ ...originForm, country_name: event.target.value })} placeholder="Country" />
                                    </div>
                                    <div className="col-md-3 mb-2">
                                        <label>Value of Goods</label>
                                        <input className="form-control" value={originForm.goods_value_above} onChange={(event) => setOriginForm({ ...originForm, goods_value_above: event.target.value })} placeholder="Value of Goods" />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <label>Chargeable Wt</label>
                                        <input className="form-control" value={originForm.chargeable_weight_above} onChange={(event) => setOriginForm({ ...originForm, chargeable_weight_above: event.target.value })} placeholder="Chargeable Wt" />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <label>Charge (AED)</label>
                                        <input className="form-control" value={originForm.charge_amount} onChange={(event) => setOriginForm({ ...originForm, charge_amount: event.target.value })} placeholder="Charge" />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <button type="submit" className="btn btn-primary w-100">Save</button>
                                    </div>
                                </form>
                                <div className="d-flex flex-wrap align-items-center mb-3">
                                    <input
                                        className="form-control"
                                        style={{ maxWidth: 320 }}
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        placeholder="Search"
                                    />
                                    <button type="button" className="btn btn-success mx-2" onClick={() => downloadOriginList(chargeMode, rows)} disabled={!rows.length}>Download list</button>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-centered table-nowrap mb-0 border text-center">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Country</th>
                                                <th>Value of Goods</th>
                                                <th>Chargeable Wt</th>
                                                <th>Charge (AED)</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleRows.length ? visibleRows.map((row) => (
                                                <tr key={row._id}>
                                                    <td>{row.country_name}</td>
                                                    <td>Above {plain(row.goods_value_above)}</td>
                                                    <td>Above {plain(row.chargeable_weight_above)} kgs</td>
                                                    <td>{plain(row.charge_amount)}</td>
                                                    <td>
                                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(row, 'certificate_of_origin')}>Remove</button>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="5">{rows.length ? 'No matching country.' : 'No countries yet. Upload the CSV or add one country.'}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                        {chargeMode === 'courier' && tab === 'remote' && (
                            <>
                                <p className="mb-3">
                                    Put each country name on the first row. Put the postal code and tier on the rows below that country.
                                    The price per kg and the minimum come from the Tier Price tab.
                                    Uploading adds new postal codes and updates a postal code already in the file. Other saved postal codes stay.
                                </p>
                                <div className="d-flex flex-wrap justify-content-end mb-3">
                                    <button type="button" className="btn btn-outline-secondary mx-1" onClick={() => downloadRemoteSheet(true)}>Sample CSV</button>
                                    <input id="remote-upload" type="file" accept=".csv,.xlsx,.xls" hidden onChange={(event) => onUpload(event, 'remote_surcharge')} />
                                    <label htmlFor="remote-upload" className="btn btn-danger mx-1 mb-0">Upload file</label>
                                </div>
                                <form className="row align-items-end mb-4" onSubmit={onSaveRemote}>
                                    <div className="col-md-3 mb-2">
                                        <label>Country</label>
                                        <input className="form-control" value={remoteForm.country_name} onChange={(event) => setRemoteForm({ ...remoteForm, country_name: event.target.value })} placeholder="Country" />
                                    </div>
                                    <div className="col-md-3 mb-2">
                                        <label>Postal code</label>
                                        <input className="form-control" value={remoteForm.postal_code} onChange={(event) => setRemoteForm({ ...remoteForm, postal_code: event.target.value })} placeholder="Postal code" />
                                    </div>
                                    <div className="col-md-3 mb-2">
                                        <label>Tier</label>
                                        <select className="form-control" value={remoteForm.tier} onChange={(event) => setRemoteForm({ ...remoteForm, tier: event.target.value })}>
                                            <option value="">Tier</option>
                                            {tierOptions.map((row) => (
                                                <option key={row._id || row.tier} value={row.tier}>
                                                    {row.tier} — {money(row.charge_amount)}/kg, min {money(row.minimum_amount)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <button type="submit" className="btn btn-primary w-100">Save</button>
                                    </div>
                                </form>
                                <div className="d-flex flex-wrap align-items-center mb-3">
                                    <input
                                        className="form-control"
                                        style={{ maxWidth: 320 }}
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        placeholder="Search"
                                    />
                                    <button type="button" className="btn btn-success mx-2" onClick={() => downloadRemoteSheet(false)} disabled={!rows.length}>Download list</button>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-bordered text-center mb-0 align-middle" style={{ width: 'auto' }}>
                                        {remoteGroups.length ? (
                                            <>
                                                <thead>
                                                    <tr>
                                                        {remoteGroups.map(([name]) => (
                                                            <th key={name} colSpan={3} className="align-middle" style={{ background: '#f3f4f6' }}>{name}</th>
                                                        ))}
                                                    </tr>
                                                    <tr className="table-light">
                                                        {remoteGroups.map(([name]) => (
                                                            <React.Fragment key={name}>
                                                                <th className="align-middle" style={{ minWidth: 150 }}>Postal code</th>
                                                                <th className="align-middle" style={{ minWidth: 90 }}>Tier</th>
                                                                <th style={{ width: 110 }}></th>
                                                            </React.Fragment>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {Array.from({ length: remoteLongest }).map((_, index) => (
                                                        <tr key={index}>
                                                            {remoteGroups.map(([name, list]) => {
                                                                const row = list[index];
                                                                if (!row) {
                                                                    return (
                                                                        <React.Fragment key={`${name}-${index}`}>
                                                                            <td></td>
                                                                            <td></td>
                                                                            <td></td>
                                                                        </React.Fragment>
                                                                    );
                                                                }
                                                                return (
                                                                    <React.Fragment key={row._id}>
                                                                        <td className="align-middle">{row.postal_code}</td>
                                                                        <td className="align-middle">
                                                                            {row.tier}
                                                                            {tierPrice(row.tier) && (
                                                                                <div className="text-muted" style={{ fontSize: '12px' }}>
                                                                                    {money(tierPrice(row.tier).charge_amount)}/kg, min {money(tierPrice(row.tier).minimum_amount)}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className="align-middle">
                                                                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(row, 'remote_surcharge')}>Remove</button>
                                                                        </td>
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </>
                                        ) : (
                                            <tbody>
                                                <tr>
                                                    <td>{rows.length ? 'No matching postal code.' : 'No postal codes yet. Upload the file or add one postal code.'}</td>
                                                </tr>
                                            </tbody>
                                        )}
                                    </table>
                                </div>
                            </>
                        )}
                        {chargeMode === 'courier' && tab === 'tier' && (
                            <>
                                <p className="mb-3">
                                    The remote surcharge is the price per kg, or the minimum, whichever is higher.
                                    Adding a tier that is already in the list updates its price and minimum.
                                </p>
                                <form className="row align-items-end mb-4" onSubmit={onSaveTier}>
                                    <div className="col-md-3 mb-2">
                                        <label>Tier</label>
                                        <input className="form-control" value={tierForm.tier} onChange={(event) => setTierForm({ ...tierForm, tier: event.target.value })} placeholder="Tier" />
                                    </div>
                                    <div className="col-md-3 mb-2">
                                        <label>Price per kg (AED)</label>
                                        <input className="form-control" value={tierForm.charge_amount} onChange={(event) => setTierForm({ ...tierForm, charge_amount: event.target.value })} placeholder="Price per kg" />
                                    </div>
                                    <div className="col-md-3 mb-2">
                                        <label>Minimum (AED)</label>
                                        <input className="form-control" value={tierForm.minimum_amount} onChange={(event) => setTierForm({ ...tierForm, minimum_amount: event.target.value })} placeholder="Minimum" />
                                    </div>
                                    <div className="col-md-2 mb-2">
                                        <button type="submit" className="btn btn-primary w-100">Add</button>
                                    </div>
                                </form>
                                <div className="d-flex flex-wrap align-items-center mb-3">
                                    <input
                                        className="form-control"
                                        style={{ maxWidth: 320 }}
                                        value={search}
                                        onChange={(event) => setSearch(event.target.value)}
                                        placeholder="Search"
                                    />
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-centered table-nowrap mb-0 border text-center">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Tier</th>
                                                <th>Price per kg (AED)</th>
                                                <th>Minimum (AED)</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleRows.length ? visibleRows.map((row) => (
                                                <tr key={row._id}>
                                                    <td>{row.tier}</td>
                                                    <td>{money(row.charge_amount)}</td>
                                                    <td>{money(row.minimum_amount)}</td>
                                                    <td>
                                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => onRemove(row, 'remote_tier')}>Remove</button>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="4">{rows.length ? 'No matching tier.' : 'No tiers yet. Add a tier, its price per kg, and its minimum.'}</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const CourierModeCharges = () => <ModeCharges mode="courier" />;

export default CourierModeCharges;
